import { Module } from '../../../../shared/objects/auth/module';
import { CpModule } from '../../../../shared/objects/auth/cpModule';
import { Injectable } from '@angular/core';
import { UserRole } from '../../../../shared/objects/auth/userRole';
import { AuthService } from '../../../../core/services/auth.service';

@Injectable()
export class ModuleService {
  readonly administrationTemplate: Module[] = [];
  readonly featureDashboardModules: Module[] = [];
  readonly defaultDashboardModules: Module[] = [];
  constructor(private authService: AuthService) {
    this.administrationTemplate.push(
      ...[
        Module.ApproveCarriers,
        Module.Carriers,
        Module.Users,
        Module.PortalSettings,
      ],
    );
    this.featureDashboardModules.push(
      ...[
        Module.Orders,
        Module.FreightExchange,
        Module.Selfbillings,
        Module.Tenders,
        Module.ExWorkBooking,
        Module.Incidents,
      ],
    );
    this.defaultDashboardModules.push(...[Module.InstructionDocs]);
  }

  getAdministrationTemplate(): Module[] {
    return this.administrationTemplate;
  }

  getFeatureDashboardModules(claims: Module[]): CpModule[] {
    const featureModules: CpModule[] = this.constructTheFeaturesTemplate();
    claims.forEach(claim => {
      const module = featureModules.find(fm => fm.name === claim.toString());
      if (module) {
        module.available = true;
      }
    });

    return featureModules;
  }

  getAdministrationDashboardModules(claims: Module[]): CpModule[] {
    const administrationModules: CpModule[] =
      this.constructTheAdministrationTemplate();

    claims.forEach(claim => {
      const module = administrationModules.find(
        fm => fm.name === claim.toString(),
      );
      if (module) {
        module.available = true;
      }
    });

    return administrationModules;
  }

  getAvailableModules(): CpModule[] {
    const availableModules: CpModule[] = [];
    const featureClaims: Module[] = this.authService.getUserFeatureClaims();
    const administrationClaims: Module[] =
      this.authService.getUserAdministrationClaims();

    if (featureClaims) {
      availableModules.push(...this.getFeatureDashboardModules(featureClaims));
    }

    if (administrationClaims) {
      availableModules.push(
        ...this.getAdministrationDashboardModules(administrationClaims),
      );
    }

    const userRole = this.authService.getUserRole();

    if (administrationClaims) {
      const portalSettings = availableModules.pop();
      availableModules.push(...this.getDefaultDashboardFeatures(userRole));
        availableModules.push(portalSettings);
    } else {
      availableModules.push(...this.getDefaultDashboardFeatures(userRole));
    }

    if (
      userRole === UserRole.PortalAdmin ||
      userRole === UserRole.BusinessAdmin
    ) {
      return this.createDashboardForAdministrator(availableModules);
    } else {
      return this.createDashboardForUser(availableModules);
    }
  }

  getUserFeatureModulesBasedOnCarrier(
    carrierModules: Module[],
    userEnabledModules: Module[],
  ): CpModule[] {
    const userModules: CpModule[] = [];
    carrierModules.forEach(carrierModule => {
      userModules.push(
        this.getModule(
          carrierModule,
          !!userEnabledModules &&
            userEnabledModules.indexOf(carrierModule) !== -1,
          !this.defaultDashboardModules.includes(carrierModule),
        ),
      );
    });

    return userModules;
  }

  private createDashboardForAdministrator(modules: CpModule[]): CpModule[] {
    return modules.filter(
      module => module.available === true && !this.isFeatureModule(module),
    );
  }

  private createDashboardForUser(modules: CpModule[]): CpModule[] {
    return modules.filter(
      module =>
        !this.isAdministrationModule(module) ||
        (this.isAdministrationModule(module) && module.available === true),
    );
  }

  private isAdministrationModule(module: CpModule): Boolean {
    return (
      this.administrationTemplate.findIndex(
        mod => mod.toString() === module.name.toString(),
      ) !== -1
    );
  }

  private isFeatureModule(module: CpModule): Boolean {
    return (
      this.featureDashboardModules.findIndex(
        mod => mod.toString() === module.name.toString(),
      ) !== -1
    );
  }

  constructTheFeaturesTemplate(): CpModule[] {
    const tmpList: CpModule[] = [];
    tmpList.push(this.getModule(Module.Orders, false, true));
    tmpList.push(this.getModule(Module.FreightExchange, false, true));
    tmpList.push(this.getModule(Module.Selfbillings, false, true));
    tmpList.push(this.getModule(Module.Tenders, false, true));
    tmpList.push(this.getModule(Module.ExWorkBooking, false, true));
    tmpList.push(this.getModule(Module.Incidents, false, true));

    return tmpList;
  }

  private constructTheAdministrationTemplate(): CpModule[] {
    const tmpList: CpModule[] = [];
    tmpList.push(this.getModule(Module.ApproveCarriers, false, true));
    tmpList.push(this.getModule(Module.Carriers, false, true));
    tmpList.push(this.getModule(Module.Users, false, true));
    tmpList.push(this.getModule(Module.PortalSettings, false, true));

    return tmpList;
  }

  private getModule(
    key: Module,
    availability: boolean,
    isMainModule: boolean,
  ): CpModule {
    switch (key) {
      case Module.Orders:
        return new CpModule(
          key.toString(),
          '/secure/order-list',
          availability,
          'assets/dashboard/orderlist.png',
          'DASHBOARD.ORDER',
          isMainModule,
        );
      case Module.FreightExchange:
        return new CpModule(
          key.toString(),
          '/secure/freight-exchange',
          availability,
          'assets/dashboard/delivery-truck.png',
          'DASHBOARD.FREIGHTEXCHANGE',
          isMainModule,
        );
      case Module.Selfbillings:
        return new CpModule(
          key.toString(),
          '/secure/self-billing',
          availability,
          'assets/dashboard/touch-screen.png',
          'DASHBOARD.SELFBILLING',
          isMainModule,
        );
      case Module.Tenders:
        return new CpModule(
          key.toString(),
          '/secure/tender',
          availability,
          'assets/dashboard/devices.png',
          'DASHBOARD.TENDERS',
          isMainModule,
        );
      case Module.ExWorkBooking:
        return new CpModule(
          key.toString(),
          '/secure/ex-work-timeslot',
          availability,
          'assets/dashboard/credit-card.png',
          'DASHBOARD.EXWORKTIMESLOT',
          isMainModule,
        );
      case Module.ApproveCarriers:
        return new CpModule(
          key.toString(),
          '/secure/admin/approve-carriers',
          false,
          'assets/dashboard/resume.png',
          `DASHBOARD.APPROVE-CARRIERS`,
          isMainModule,
        );
      case Module.PortalSettings:
        return new CpModule(
          key.toString(),
          '/secure/admin/portal-settings',
          false,
          'assets/dashboard/settings.png',
          `DASHBOARD.PORTAL-SETTINGS`,
          isMainModule,
        );
      case Module.Carriers:
        return new CpModule(
          key.toString(),
          '/secure/admin/carriers',
          false,
          'assets/dashboard/connection.png',
          `DASHBOARD.CARRIERS`,
          isMainModule,
        );
      case Module.Users:
        return new CpModule(
          key.toString(),
          '/secure/admin/users',
          false,
          'assets/dashboard/team.png',
          `DASHBOARD.USERS`,
          isMainModule,
        );
      case Module.InstructionDocs:
        return new CpModule(
          key.toString(),
          '/secure/instruction-docs',
          availability,
          'assets/dashboard/instruction-docs.png',
          `DASHBOARD.INSTRUCTION-DOCS`,
          isMainModule,
        );
      case Module.Incidents:
        return new CpModule(
          key.toString(),
          '/secure/incidents',
          availability,
          'assets/dashboard/incident.png',
          `DASHBOARD.INCIDENTS`,
          isMainModule,
        );
    }
  }

  private getDefaultDashboardFeatures(userRole: UserRole): CpModule[] {
    const defaultModules: CpModule[] = [];
    this.defaultDashboardModules.forEach(module => {
      defaultModules.push(this.getModule(module, true, false));
    });
    return defaultModules;
  }
}
